<script>
import metricComponentsMixin from '../../mixins/metricComponentsMixin.js';
import InboxName from 'dashboard/components/widgets/InboxName.vue';
import { useAlert } from 'dashboard/composables';

// Display components
import SummaryIndicator from '../../displayComponents/Summary.vue';
import DonutChart from './Donut.vue';
import ColumnChart from './ColumnChart.vue';

// POSSIBILIDADES DE DADOS PARA MÉTRICA DE CONVERSAS
const REPORTS_KEYS = {
  INCOMING_MESSAGES: 'incoming_messages_count',
  OUTGOING_MESSAGES: 'outgoing_messages_count',
  // REPLY_TIME: 'reply_time',
};
// Usar essa variável quando quiser testar algum layout
// eslint-disable-next-line no-unused-vars
const MOCK_DIAS_DA_SEMANA = {
  incomingMessagesReport: [
    { value: 158, timestamp: 1721962800 },
    { value: 123, timestamp: 1722049200 },
    { value: 231, timestamp: 1722135600 },
    { value: 100, timestamp: 1722222000 },
    { value: 30, timestamp: 1722308400 },
    { value: 120, timestamp: 1722394800 },
    { value: 330, timestamp: 1722481200 },
  ],
  outgoingMessagesReport: [
    { value: 50, timestamp: 1721962800 },
    { value: 203, timestamp: 1722049200 },
    { value: 301, timestamp: 1722135600 },
    { value: 152, timestamp: 1722222000 },
    { value: 148, timestamp: 1722308400 },
    { value: 119, timestamp: 1722394800 },
    { value: 163, timestamp: 1722481200 },
  ],
};

export default {
  components: {
    SummaryIndicator,
    InboxName,
    DonutChart,
    ColumnChart,
  },
  mixins: [metricComponentsMixin],
  props: {
    id: {
      type: String,
      required: true,
    },
    display: {
      type: Object,
      required: true,
    },
    // Current cell size
    sizeX: {
      type: Number,
      required: true,
    },
    sizeY: {
      type: Number,
      required: true,
    },
    dateRange: {
      type: Object,
      required: true,
    },
    inbox: {
      type: Object,
      default: () => ({}),
    },
    widgetVisualizationOptions: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Caso seja preciso acessar algum dado externo devemos utilizar props
     * Essas props devem ser declaradas dentro de 'contentProps' nos componentes pai:
     *    - CreateCustomDashboards
     *    - EditCustomDashboard
     * Também é possível adicionar no EditWidgetModal.vue, basta acrescentar o valor como parte do objeto que é enviado para a função 'edit-current-widget'
     */
  },
  data() {
    const translatedOptions = this.translatedDateRangeOptions();

    return {
      selectedDateRange: translatedOptions[0],
      options: translatedOptions,

      editingWidget: false, // Customer is on '/edit' page and is editing current widget

      isLoading: true,
      errorInReq: false,

      outgoingCount: 0,
      incomingCount: 0,
      accountReport: {
        incomingMessagesReport: {},
        outgoingMessagesReport: {},
      },

      shouldShowPreview: false,

      componentWidth: 0,
    };
  },
  computed: {
    summaryContent() {
      return (this.outgoingCount + this.incomingCount).toLocaleString();
    },
    donutData() {
      return {
        outgoing: this.outgoingCount || 0,
        incoming: this.incomingCount || 0,
      };
    },
    widgetContentHeightClass() {
      // Checking if widget is displaying the descriptions
      const { metric, dateRange, inbox } = this.widgetVisualizationOptions;
      const showingDescriptions = Boolean(
        metric || dateRange || (inbox && this.inbox?.id)
      );
      return showingDescriptions ? 'h-[calc(100%-40px)]' : 'h-full';
    },
  },
  mounted() {},
  methods: {
    setInitialData() {
      const { type } = this.dateRange;
      this.selectedDateRange = this.translatedDateRangeOptions().find(
        e => e.id === type
      );
    },

    // Essa função é a responsável por chamar a função de request correta de acordo com o tipo de dado que precisamos
    // Exemplo: O Summary pode precisar de um valor (retorno de request) diferente de um gráfico de barras
    async fetchData() {
      if (this.isSummaryDisplay) await this.fetchSummaryAccountData();
      if (this.isDonutDisplay) await this.fetchSummaryAccountData();
      if (this.isColumnChartDisplay) await this.fetchBarData();

      // Se for outros displays adicionar aqui as condições
      this.isLoading = false;
    },

    showPreview() {
      this.shouldShowPreview = true;
      this.fetchData();
    },

    async fetchBarData() {
      await Promise.all([
        this.fetchDataWithinAccountReport('INCOMING_MESSAGES'),
        this.fetchDataWithinAccountReport('OUTGOING_MESSAGES'),
      ]);
      // MOCK DE DIAS DA SEMANA
      // this.accountReport = MOCK_DIAS_DA_SEMANA;
    },

    async fetchSummaryAccountData() {
      const payload = this.getRequestPayload();
      if (this.inbox.id) {
        payload.type = 'inbox';
        payload.id = this.inbox.id;
      }
      try {
        const data = await this.$store.dispatch('fetchAccountSummary', payload);
        this.incomingCount = data.incoming_messages_count;
        this.outgoingCount = data.outgoing_messages_count;
      } catch {
        useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
        this.errorInReq = true;
      }
    },
    async fetchDataWithinAccountReport(key) {
      const payload = {
        metric: REPORTS_KEYS[key],
        ...this.getRequestPayload(),
      };
      if (this.inbox.id) {
        payload.type = 'inbox';
        payload.id = this.inbox.id;
      }
      try {
        const data = await this.$store.dispatch('fetchAccountReport', payload);
        if (key === 'INCOMING_MESSAGES')
          this.accountReport.incomingMessagesReport = data;
        if (key === 'OUTGOING_MESSAGES')
          this.accountReport.outgoingMessagesReport = data;
      } catch {
        useAlert(this.$t('REPORT.DATA_FETCHING_FAILED'));
      }
    },
  },
};
</script>

<template>
  <div
    ref="widget"
    class="max-h-full h-full overflow-auto pt-3 px-3 widget-container"
  >
    <div class="w-full flex relative">
      <div class="flex gap-2 max-w-[calc(100%-40px)] flex-wrap">
        <!-- NAME OF SELECTED METRIC -->
        <div
          v-if="widgetVisualizationOptions.metric"
          class="widget_preferences_tags bg-slate-50 border-slate-50"
        >
          {{ $t('CUSTOM_DASHBOARDS.WIDGET_TYPES.MESSAGES') }}
        </div>
        <!-- SELECTED DATE RANGE -->
        <div
          v-if="widgetVisualizationOptions.dateRange"
          class="widget_preferences_tags bg-woot-50 border-woot-50"
        >
          <span class="text-[black]">
            {{ $t('CUSTOM_DASHBOARDS.EDIT_WIDGET.INTERVAL') }}
          </span>
          &nbsp;{{ dateRangeDescription }}
        </div>
        <!-- INBOX NAME (IF SELECTED) -->
        <div
          v-if="inbox.id && widgetVisualizationOptions.inbox"
          class="widget_preferences_tags bg-yellow-100 border-yellow-100"
        >
          <InboxName
            :inbox="currentInbox"
            :text-length="30"
            class="ltr:ml-0 rtl:mr-0 mb-0 m-0 mr-0 text-slate-800 text-xxs"
          />
        </div>
      </div>

      <!-- BUTTONS EDIT/SAVE WIDGET SECTION -->
      <div v-if="canEdit" class="z-[999] absolute right-0">
        <!-- BUTTON TO OPEN EDITING SECTION -->
        <woot-button
          class="flex items-center"
          size="tiny"
          variant="smooth"
          color-scheme="secondary"
          icon="settings"
          @click="openEditModal"
        />
      </div>
    </div>

    <div
      class="w-full pb-3 flex justify-center items-center"
      :class="widgetContentHeightClass"
    >
      <!-- 
     Adicionando botão de prévia para evitar requisições desnecessárias.
     A requisição é feita apenas quando o cliente exibe o display (não está editando/criando).
     Isso resolve o problema de múltiplas requisições desnecessário ao modificar um único widget -->
      <woot-button v-if="canEdit && !shouldShowPreview" @click="showPreview">
        {{ $t('CUSTOM_DASHBOARDS.EDIT_WIDGET.VIEW_PREVIEW') }}
      </woot-button>
      <div v-else>
        <!-- SPINNER WAITING REQUEST FINISHES -->
        <woot-loading-state
          v-if="isLoading"
          class="text-xxs"
          :message="$t('REPORT.LOADING_CHART')"
        />
        <!-- WHEN REQUISITION UNSUCCESSFUL -->
        <div v-else-if="errorInReq" class="text-red-600 text-xs">
          ⚠️ {{ $t('REPORT.DATA_FETCHING_FAILED') }}
        </div>
        <!-- DATA BEING DISPLAYED -->
        <div v-else>
          <SummaryIndicator
            v-if="isSummaryDisplay"
            :size-x="sizeX"
            :size-y="sizeY"
            :content="summaryContent"
          />
          <DonutChart
            v-else-if="isDonutDisplay"
            :size-x="sizeX"
            :size-y="sizeY"
            :current-width="componentWidth"
            :data="donutData"
          />
          <ColumnChart
            v-else-if="isColumnChartDisplay"
            :size-x="sizeX"
            :size-y="sizeY"
            :current-width="componentWidth"
            :show-detailed-msgs="
              widgetVisualizationOptions.showDetailedMsgsColChart
            "
            :group-by="groupBy"
            :data="accountReport"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.widget-container > div {
  @apply text-slate-700;
}
.widget_preferences_tags {
  @apply text-xxs leading-none p-2 rounded-full text-center flex justify-center items-center border border-solid;
}
</style>
